import moment from 'moment'
import noCard from '@/assets/images/no-card.svg'
import { NO_CARD_IMAGE_URL, CARD_FIELD_LABELS } from '@/constants'
import { cleanStatus } from '@/helpers/text'
import { validSportTypes } from '@/helpers/sportTypes'
import { useCardStore } from '@/stores/CardStore'

/**
 * Resolve a card field key to its user-facing label. Prefers the curated
 * `CARD_FIELD_LABELS`; falls back to the BE message's own prefix (text before
 * the first ":"), then a title-cased field key.
 */
export const cardFieldLabel = (field, message = '') => {
  if (CARD_FIELD_LABELS[field]) return CARD_FIELD_LABELS[field]

  const prefix = String(message).split(':')[0]?.trim()

  if (prefix && prefix !== String(message)) return prefix

  return field ? field.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : ''
}

/**
 * Build the "Missing information" tooltip items from a card's `errors` and
 * `suggested_edits` maps ({ field: verboseBackendMessage }). Errors become
 * `required`, suggested edits `suggested`; errors come first (matching Figma).
 * The verbose BE copy is intentionally dropped — the tooltip shows only
 * `<Column>: required|suggested`.
 * → [{ field, label, kind: 'required' | 'suggested' }]
 */
export const missingInfoItems = (errors, suggestedEdits) => {
  const items = []

  Object.entries(errors || {}).forEach(([field, msg]) => 
    items.push({ field, label: cardFieldLabel(field, msg), kind: 'required' }))

  Object.entries(suggestedEdits || {}).forEach(([field, msg]) =>
    items.push({ field, label: cardFieldLabel(field, msg), kind: 'suggested' }))

  return items.filter(item => item.label)
}

/**
 * Cards whose images changed since this page loaded.
 *
 * The `?_t=` suffix used to be stamped on EVERY image URL with `Date.now()`,
 * evaluated per call — so every URL was unique on every render and the browser
 * could never reuse anything, re-downloading multi-megabyte originals as the
 * dealer scrolled. It was only ever meant as a cache-buster for the one case
 * that needs it: an image the dealer just ROTATED or REPLACED, where the file
 * behind an unchanged URL really has changed.
 *
 * So the bust is now opt-in and per card. A plain Map, not store state, because
 * `CardStore` imports this module — a store import here would be a cycle — and
 * reactivity isn't needed: the operations that stamp a card also refetch it, so
 * the value is always written before `generateCardData` reads it.
 */
const imageVersions = new Map()

/** Call after rotating / replacing a card's image so its URLs reload once. */
export const bustCardImageCache = (cardId) => {
  if (cardId === null || cardId === undefined) return

  imageVersions.set(String(cardId), Date.now())
}

const cacheBust = (url, cardId) => {
  const version = imageVersions.get(String(cardId))

  if (!url || !version) return url

  return `${url}${url.includes('?') ? '&' : '?'}_t=${version}`
}

/**
 * The `-sm` / `-md` derivative of one of OUR uploads.
 *
 * `…/74459641-front.jpg` → `…/74459641-front-md.jpg`. Only our own bucket has
 * derivatives, so CollX match images, the no-card placeholder and anything else
 * are returned untouched rather than pointed at a file that doesn't exist.
 */
const SIZED_URL_PATTERN = /(-(?:sm|md))?(\.[a-z0-9]+)(\?.*)?$/i

export const withImageSize = (url, size) => {
  if (!url || !size || !/\/cdp-batches/.test(url)) return url

  return url.replace(SIZED_URL_PATTERN, `-${size}$2$3`)
}

/**
 * The URL to render for one side of a card, at one size.
 *
 * `size` is 'sm' for thumbnails, 'md' for everything the dealer actually looks
 * at, and null only where the ORIGINAL is genuinely required. Originals are
 * multi-megabyte; nothing in the UI should be reaching for one.
 *
 * The API's own `*_image_sm` / `*_image_md` fields win when present; otherwise
 * the derivative is built from the original, which is how these URLs were
 * derived before those fields existed. Falling back to the ORIGINAL rather than
 * to the placeholder matters: a card whose derivatives haven't been generated
 * yet should show its picture, not "no image".
 */
export const getImage = (card, mainImageField, fieldName, size = null) => {
  const noImages = !card.front_image && !card.back_image
  const isBack = fieldName?.includes('back_image') || mainImageField?.includes('back_image')

  const sized = size ? card[`${mainImageField}_${size}`] : null
  const own = sized || card[fieldName] || (size ? withImageSize(card[mainImageField], size) : null)

  if (own) return cacheBust(size ? withImageSize(own, size) : own, card.id)

  if (noImages && card[`match_${mainImageField}`]) return card[`match_${mainImageField}`]

  return isBack ? null : NO_CARD_IMAGE_URL
}

/**
 * Can this match entry actually be RENDERED as a row / as the matched card?
 *
 * A candidate row shows set · number · player and a thumbnail. Some inventory
 * payloads carry `vision_matches` entries that are ids and positions only —
 * nothing to print. Rendering those gave a list of blank rows with a bare "#"
 * and a NO CARD IMAGE placeholder, which reads as broken results rather than as
 * "this payload didn't include the details".
 */
const canDescribeMatch = (entry) => !!(entry
  && (entry.set?.name || entry.number || entry.name || entry.player?.name))

const generateCardData = (card = {}, selectedRanges, config) => {
    let front_image = getImage(card, 'front_image', 'front_image')
    let back_image = getImage(card, 'back_image', 'back_image') 
  
    // `sm` for thumbnails, `md` for everything on screen. Both fall back to a
    // URL derived from the original when the API hasn't sent the field.
    let front_image_sm = getImage(card, 'front_image', 'front_image_sm', 'sm')
    let back_image_sm = getImage(card, 'back_image',  'back_image_sm', 'sm')

    let front_image_md = getImage(card, 'front_image', 'front_image_md', 'md')
    let back_image_md = getImage(card, 'back_image', 'back_image_md', 'md')
  
    let status = 'draft'
    if (['processing', 'ready_to_list'].includes(card.status)) {
      status = card.status
    }
  
    let matches = []
    let bestMatch = null
  
    /**
     * The match candidates arrive under DIFFERENT NAMES depending on which
     * endpoint fetched the card, and only one of them was read.
     *
     * `GET /cards/:id` returns `matches` — an object (sometimes a JSON string)
     * of `{ legacyMatches, vision, selected }`. The INVENTORY list endpoint,
     * which feeds the same grid and inspector, returns a flat `vision_matches`
     * ARRAY carrying what `matches.vision` carries. So the possible matches
     * showed while the card was open and vanished on refresh: the data was
     * there both times, under a name nothing looked at.
     *
     * Normalised to the object shape here so everything below — legacy vs
     * vision precedence, the selected-match branch, `bestMatch` — stays one
     * code path rather than growing a second per-endpoint one.
     */
    const rawMatches = card.matches ?? card.vision_matches

    if (rawMatches) {
      let matchData = null
  
      if (typeof rawMatches === 'string') {
        try {
          matchData = JSON.parse(rawMatches)
        } catch (e) {
          console.error('error parsing `matches`', card, e)
        }
      } else if (typeof rawMatches === 'object') {
        matchData = rawMatches
      }

      /**
       * A bare array is the inventory payload's `vision_matches`; give it the
       * slot the object form calls `vision`.
       *
       * Entries that can't be displayed are dropped rather than listed: where
       * this payload carries ids only, showing them produced a "N other
       * matches" button opening a list of empty rows. With none left the button
       * doesn't appear at all — the behaviour those accounts had before
       * `vision_matches` was read, and honest about what the payload contains.
       */
      if (Array.isArray(matchData)) matchData = { vision: matchData.filter(canDescribeMatch) }

      // An unparseable payload is not a crash: `matches` stays [] and the card
      // reads as unmatched, which is what it did before this block ran at all.
      matchData = matchData || {}
  
      let haveLegacyMatches = matchData.legacyMatches && matchData.legacyMatches?.length
      
      let haveVisionMatches = matchData.vision && matchData.vision?.length
  
      let haveSelectedMatches = matchData.selected && Object.keys(matchData.selected).length
  
      if (haveLegacyMatches && matchData.legacyMatches[0].frontImageUrl) {
        haveLegacyMatches = false
        haveVisionMatches = true
        matches = matchData.legacyMatches
      } else if (haveLegacyMatches) {
        matches = matchData.legacyMatches
      } else if (haveVisionMatches) {
        matches = matchData.vision 
      }
  
      // User has selected a different match, use that match
      if (card.collx_match_id && haveSelectedMatches) {
        matches = [matchData.selected]

        if (haveVisionMatches) {
          matches = matchData.vision
        }
        
        bestMatch = {
          id: matches[0].id,
          front_image: card.frontImageUrl === '' ? noCard : matches[0].frontImageUrl,
          back_image: matches[0].backImageUrl,
          title: matches[0].set?.name + ' #' + matches[0]?.number + ' ' + (matches[0]?.name || matches[0].player?.name) + (matches[0].serialNumber ? ` #/${matches[0].serialNumber}` : ''), 
          card_number: matches[0].number,
          player: matches[0]?.name || matches[0].player?.name,
          year: matches[0].year,
          set: matches[0].set?.name || matches[0].set?.subset,
          setId: card.collx_parent_set_id || matches[0].set?.id, 
          brand: matches[0].brand?.name,
          subset: matches[0].set?.subset,
          attributes: matches[0].flags?.join(', '),
          variation: matches[0].variation,
          serialNumber: matches[0].serialNumber,
        }
      }
  
      // User has not selected a match, but we have a vision match, use the first vision match
      if (card.collx_match_id && !haveSelectedMatches && haveVisionMatches) {
        // `matchData`, not `card.matches`: an inventory card carries its
        // candidates in `vision_matches` and has no `matches` object at all, so
        // reading through it here threw the moment this branch became reachable
        // for those rows — and the thrown error emptied the whole grid.
        matches = matchData.vision

        /**
         * Describe the match from a vision CANDIDATE only on the batch payload.
         *
         * `matches[0]` is the first candidate, not necessarily the matched card
         * — `collx_match_id` is what says which one that is. On the batch
         * payload the candidate list has always been the source here and the
         * behaviour is long-standing, so it stays.
         *
         * The INVENTORY payload has no `matches` object and no `selected`
         * inside it, so there is nothing there that identifies the match: it
         * carries the candidates as `vision_matches` and the matched values on
         * the CARD ITSELF. Describing it from `matches[0]` printed a literal
         * "undefined #undefined undefined" when the entry was sparse, and named
         * the wrong card when it wasn't. So that payload leaves `bestMatch`
         * null here and falls through to the card-fields fallback below.
         *
         * TEMPORARY, by agreement: a proper field identifying the matched
         * record on the inventory payload is coming. When it lands, read it
         * here and this endpoint split goes away.
         */
        const describesMatch = !!card.matches && canDescribeMatch(matches[0])

        if (describesMatch) bestMatch = {
          id: matches[0].id,
          front_image: card.frontImageUrl === '' ? noCard : card.match_front_image,
          back_image: card.backImageUrl === '' ? noCard : card.match_back_image,
          title: matches[0].set?.name + ' #' + matches[0]?.number + ' ' + (matches[0]?.name || matches[0].player?.name) + (matches[0].serialNumber ? ` #/${matches[0].serialNumber}` : ''), // XXX
          card_number: matches[0].number,
          player: matches[0].player?.name,
          year: matches[0].year,
          set: matches[0].set?.name,
          setId: card.collx_parent_set_id || matches[0].set?.id,
          brand: matches[0].brand?.name,
          subset: matches[0].set?.subset,
          attributes: matches[0].flags?.join(', '),
          variation: matches[0].variation,
          serialNumber: matches[0].serialNumber,
        }
      }
    }

    /**
     * Inventory fallback — a card IS matched, but this payload can't describe
     * the match.
     *
     * Everything above reads the match CANDIDATES, which both endpoints supply
     * (`matches` from `GET /cards/:id`, `vision_matches` from `/inventory/:id`
     * — normalised together above). What the inventory payload does NOT carry
     * is the `selected` entry, which is where the matched record's display
     * fields come from — so `bestMatch` stayed null for every inventory row and
     * `database_match` came out `{}`. `DatabaseMatch.vue` treats an empty object as UNMATCHED,
     * so every inventory card rendered as if its database link were gone, while
     * the same cards showed as matched in the batch grid. The link was never
     * gone: `collx_match_id` is right there on the card.
     *
     * `collx_match_id` is the source of truth for "is this matched". When it's
     * set but the payload gave us nothing to build from, describe the match with
     * the card's OWN fields — for a matched card they mirror the matched record
     * — plus the `match_*` images the inventory payload does carry.
     */
    if (!bestMatch && card.collx_match_id) {
      bestMatch = {
        id: card.collx_match_id,
        front_image: card.match_front_image || noCard,
        back_image: card.match_back_image || '',
        title: card.title_template || [card.set, card.number && `#${card.number}`, card.player]
          .filter(Boolean).join(' '),
        card_number: card.number,
        player: card.player,
        year: card.year,
        set: card.set,
        setId: card.collx_parent_set_id || card.collx_set_id,
        brand: card.brand,
        subset: card.subset,
        attributes: card.flags,
        variation: card.variation,
        serialNumber: card.serial_number,
      }
    }

    const generateCustomFields = (fields) => {
      if (!fields) return {}
      
      return fields.reduce((acc, item, index) => {
        acc[`custom_fields_${index}`] = item[item.value ? 'value' : 'default_value']
        return acc
      }, {})
    }
  
    let customFieldsList = []
    let customFields = {}

    if ((!card.custom_fields || !Object.keys(card.custom_fields)?.length) && config.customFields && config.customFields.length) {
      customFieldsList = config.customFields
      customFields = generateCustomFields(config.customFields || [])
    } else if ((!card.custom_fields || !card.custom_fields?.length) && (!config.customFields || !config.customFields.length)) {
      customFields = null
      customFieldsList = null
    } else if ((card.custom_fields && (card.custom_fields?.length || Object.keys(card.custom_fields)?.length)) && (!config.customFields || !config.customFields.length)) {
      customFields = generateCustomFields(typeof card.custom_fields === 'string' ? JSON.parse(card.custom_fields) : card.custom_fields || [])
      customFieldsList = card.custom_fields
    } else {
      const fields = config.customFields.reduce((acc, item) => {
        const key = cleanStatus(item.key).toLowerCase()
        const customFieldInCard = typeof card.custom_fields === 'string' 
        ? Array.isArray(JSON.parse(card.custom_fields)) ? (JSON.parse(card.custom_fields)?.find(item => cleanStatus(item.key).toLowerCase() === key)) : JSON.parse(card.custom_fields)[key]?.value
        : Array.isArray(card.custom_fields) ? card.custom_fields.find(item => cleanStatus(item.key).toLowerCase() === key) : card.custom_fields[key]

        if (customFieldInCard) {
          acc.push({ ...item, default_value: typeof card.custom_fields === 'string' || Array.isArray(card.custom_fields) ? customFieldInCard.value : card.custom_fields[key] })
        } else {
          acc.push(item)
        }
  
        return acc
      }, [])

      customFields = generateCustomFields(fields)
      customFieldsList = fields
    }
 
    let cardData = {
      id: card.id,
      viewed: card.viewed,
      is_loading: card.status === 'processing',
      has_errors: card.has_errors || (card.errors && card.errors?.length > 0),
      errors: card.errors?.notice_required || card.errors,
      has_banner: card.has_banner,
      has_suggested_edits: card.has_suggested_edits || (card.suggested_edits && card.suggested_edits?.length > 0),
      suggested_edits: card.suggested_edits || {},
      green: !card.has_errors && (card.errors && !card.errors.length > 0) && !card.has_suggested_edits && (card.suggested_edits && !card.suggested_edits.length),
      yellow: (!card.has_errors && (card.errors && !card.errors.length > 0)) && (card.has_suggested_edits || (card.suggested_edits && card.suggested_edits.length > 0)),
      red: card.has_errors || (card.errors && card.errors.length > 0),
      select: selectedRanges ? !!selectedRanges.find(item => item.id === card.id) : false,
      front_image: front_image,
      back_image: back_image,
      front_image_sm: front_image_sm,
      back_image_sm: back_image_sm,
      front_image_md: front_image_md,
      back_image_md: back_image_md,
      title: card.title,
      template_title: card.title_template,
      template_title_id: card.title_template_id,
      template_description_id: card.description_template_id,
      database_match: bestMatch || {},
      matches: matches,
      llm: card.matches?.llm,
      selected: card.matches?.selected,
      status: status,
      start_price: card.start_price,
      collx_match_id: card.collx_match_id,
      sport: card.sport ? card.sport === 'NON_SPORT' ? 'Non-Sport' : card.sport : null,
      year: card.year,
      brand: card.brand,
      player: card.player,
      card_number: card.number,
      subset: card.subset,
      attributes: card.flags,
      team: card.team,
      set: card.set,
      variation: card.variation,
      graded: card.graded,
      grader: card.grader,
      condition: card.condition,
      cert_no: card.certification_number,
      grade_name: card.grade_name,
      grade_number: card.grade_number,
      purchase_price: card.purchase_price,
      purchase_date:  card.purchase_date ? moment(card.purchase_date).format('YYYY-MM-DD') : card.purchase_date,
      note: card.note,
      inventory_location: 'FIXME',
      cabinet: card.cabinet,
      shelf: card.shelf,
      box: card.box,
      row: card.row,
      section: card.section,
      sku: card.sku,
      description: card.description,
      description_plain_text: card.description && String(card.description).replace(/<\/(li|p|div|ul|ol|h[1-6])>/gi, '\n').replace(/<br\s*\/?>/gi, '\n').replace(/<[^>]+>/gi, '').replace(/[ \t]+/g, ' ').replace(/\n{2,}/g, '\n').trim(),
      template_description_plain_text: card.description_template && String(card.description_template)?.replace(/<style[\s\S]*?<\/style>/gi, '')?.replace(/<\/(li|p|div|ul|ol|h[1-6])>/gi, '\n')?.replace(/<br\s*\/?>/gi, '\n')?.replace(/<[^>]+>/gi, '')?.replace(/[ \t]+/g, ' ')?.replace(/\n{2,}/g, '\n')?.trim(),
      template_description: card.description_template,
      type: card.type,
      customFieldsListForInspector: customFieldsList,
      frontOrientation: card.front_orientation,
      backOrientation: card.back_orientation,
      serial_number: card.serial_number,
      cert_data: card.cert_data,
      collx_listed_at: card.collx_listed_at || card.listing?.collx_listed_at,
      ebay_listed_at: card.ebay_listed_at || card.listing?.ebay_listed_at,
      whatnot_listed_at: card.whatnot_listed_at || card.listing?.whatnot_listed_at,
      shopify_listed_at: card.shopify_listed_at || card.listing?.shopify_listed_at,
      ebay_offer_id: card.ebay_offer_id,
      inventoryStatus: card.inventoryStatus || null,
      quantity: card.quantity,
      batch_id: card.batch_id,
      restockable: card.restockable,
      listingId: card.listingId,
      created_at: card.created_at,
      updated_at: card.updated_at,
      collxListedStatus: card.collxListedStatus || (card.listing && card.listing.collx_status),
      ebayListedStatus: card.ebayListedStatus || (card.listing && card.listing.ebay_status),
      whatnotListedStatus: card.whatnotListedStatus || (card.listing && card.listing.whatnot_status),
      shopifyListedStatus: card.shopifyListedStatus || (card.listing && card.listing.shopify_status),
      readonlyListing: card.readonlyListing,
      availableQuantity: card.quantity_available || card.quantity,
      ebayListingId: card.ebayListingId,
      whatnotListingId: card.whatnotListingId,
      collx_price: card.collx_price,
      ebay_price: card.ebay_price,
      whatnot_price: card.whatnot_price,
      shopify_price: card.shopify_price,
      card_origin: !card.card_origin || card.card_origin === 'cdp' ? null : card.card_origin.split('-')[2],
      listing: card.listing,
      has_price_recommendation: card.has_price_recommendation,
      price_recommendation: card.price_recommendation,
      weight_oz: card.weight_oz,
      ...customFields
    }
  
    return cardData  
  }

  const isValidCert = (card, selectedGrader) => {
    if (['cgc', 'sgc', 'bgs', 'bvg', 'bccg'].includes(selectedGrader)) return true

    if (['psa'].includes(selectedGrader)) {
      return !!(Number(card)) && (card.length === 8 || card.length === 9)
    }
  }

  const getProperSport = (sport) => {
    if (validSportTypes[sport]) return validSportTypes[sport]
    return sport
  }

  const getSendLabel = (selectedCards, connectionKey, connectionName, labelType) => {
    if (!selectedCards || !Object.keys(selectedCards).length) return `List to ${connectionName}`

    const labels = {
      send: {
        send: `List to ${connectionName}`,
        sendAndUpdate: `Send/Update to ${connectionName}`,
        publish: `Update ${connectionName}`,
      },
      sending: {
        send: `Sending to ${connectionName}`,
        sendAndUpdate: `Sending/Updating to ${connectionName}`,
        publish: `Updating ${connectionName}`,
      },
      sent: {
        send: 'Listed',
        sendAndUpdate: 'Listed',
        publish: 'Listed',
      }
    }

    let alreadyListed = false
    let notListed = false

    for(const prop in selectedCards) {
      if (selectedCards[prop][`${connectionKey}ListedStatus`] === 'listed') {
        alreadyListed = true
      }

      if (selectedCards[prop][`${connectionKey}ListedStatus`] !== 'listed') {
        notListed = true
      }
    }

    if (alreadyListed && notListed) {
      return labels[labelType].sendAndUpdate
    }

    if (alreadyListed) {
      return labels[labelType].publish
    }

    if (notListed) {
      return labels[labelType].send
    }
  }

  const getUniqueCardTypes = (selectedCards) => {
    const cardsToCheck = selectedCards
    const types = []
    
    // Collect unique card types
    for (const card in cardsToCheck) {
      const cardType = cardsToCheck[card]?.type
      if (cardType && !types.includes(cardType)) {
        types.push(cardType)
      }
    }
    
    return types
  }

  const getCalculationBulkPriceFormula = {
    'Increase$': (price, fixedAmountToPrice) => Number(price) + Number(fixedAmountToPrice),
    'Increase%': (price, percentToPrice) => Number(price) * (1 + Number(percentToPrice) / 100),
    'Decrease$': (price, fixedAmountToPrice) => Number(price) - Number(fixedAmountToPrice),
    'Decrease%': (price, percentToPrice) => Number(price) * (1 - Number(percentToPrice) / 100),
  }

  const getBulkSalesValues = (options, startPrice) => {
    const { cards, selectedCalculation = null, selectedMethod = null, calculatedPriceValue = null, roundToNearestEnabled = null, roundToNearestType = null, minimalPriceEnabled = null, minimalPriceValue = null, ignoreAdjustments = null, useSuggestedPrice = null, usePurchasePrice = null } = options
    const cardStore = useCardStore()

    // "Round to nearest" and "Add minimal price" are checkboxes about the FINAL
    // price, not about the increase/decrease formula: they used to be applied
    // only on the formula branch, so "Apply Market Price" + a $2 minimum sent
    // the raw suggested price and left the sub-$2 cards below the minimum.
    // Every branch that produces a price runs through here now.
    const applyPriceGuards = (price) => {
      let result = Number(price)

      if (Number.isNaN(result)) return price

      if (roundToNearestEnabled && roundToNearestType === 'Dollar') {
        result = Math.round(result)
      } else if (roundToNearestEnabled && roundToNearestType === 'Half Dollar') {
        result = Math.round(result / 0.5) * 0.5
      }

      if (minimalPriceEnabled && Number(minimalPriceValue) > result) {
        result = Number(minimalPriceValue)
      }

      return result
    }

    return cards.reduce((acc, card) => {
      // `cards` usually comes from `selectedCards`, which stores the row object
      // captured WHEN THE ROW WAS SELECTED. Every refetch (including the one
      // right after a bulk price update) replaces `cardStore.cards` with new
      // objects, leaving those snapshots detached and holding the OLD price —
      // so a second "increase by $0.07" on the same selection recomputed from
      // the pre-update price and re-sent the price the item already had.
      // Always adjust the LIVE row when we can still find it.
      const cardFromBulk = cardStore.cards.find(item => item.id === card.id)
      const card_ = cardFromBulk || card

      // Per-card base price: scoped to this iteration so a card that is no
      // longer on the page can't leak the previous card's price into the next.
      let startPrice_ = startPrice

      if (cardFromBulk && cardStore.anotherPriceSource) {
        startPrice_ = cardStore.anotherPriceSource === 'suggested' ? cardFromBulk.price_recommendation?.suggested_price : cardFromBulk.purchase_price
      }

      if (usePurchasePrice && card_.purchase_price) {
        acc.push({
          id: card_.id,
          value: applyPriceGuards(card_.purchase_price)
        })
      } else if (useSuggestedPrice && card_.has_price_recommendation && card_.price_recommendation?.suggested_price) {
        const price = card_.price_recommendation?.suggested_price
        acc.push({
          id: card_.id,
          value: applyPriceGuards(price)
        })
      } else if (card_.start_price && selectedCalculation) {
        if (ignoreAdjustments) {
          acc.push({
            id: card_.id,
            value: applyPriceGuards(startPrice_)
          })
          return acc
        }


        let result = getCalculationBulkPriceFormula[`${selectedCalculation}${selectedMethod}`](startPrice_ || card_.start_price, calculatedPriceValue)

        result = applyPriceGuards(result)

        // A price the API sent back formatted ("1,000.00") makes the formula
        // NaN; `NaN.toFixed(2)` is the truthy string "NaN", which used to pass
        // the filter below and get POSTed. Skip the card instead.
        if (Number.isNaN(Number(result))) return acc

        acc.push({
          id: card_.id,
          value: Number(result).toFixed(2)
        })
      } else {
        // No price to copy for this card (e.g. "Apply Market Price" on a card
        // with no recommendation): it used to be dropped from the payload and
        // so stayed UNDER the minimum the user just set. A minimum is a floor
        // on every selected card, so fall back to its current price and let
        // the floor lift it. Rounding deliberately doesn't get this fallback —
        // it would move prices the user gave no new value for.
        const basePrice = startPrice_ || (minimalPriceEnabled ? card_.start_price : startPrice_)

        acc.push({
          id: card_.id,
          value: applyPriceGuards(basePrice)
        })
      }

      return acc
    }, []).filter(item => item.value)
  }

  export {
    generateCardData,
    isValidCert,
    getProperSport,
    getSendLabel,
    getUniqueCardTypes,
    getCalculationBulkPriceFormula,
    getBulkSalesValues
  }


